import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import RedisennoTrazabilidadVista from "./RedisennoTrazabilidadVista";

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const getUsuarioSesion = () => {
  try {
    const u = localStorage.getItem("user");
    return u ? atob(u) : "sistema";
  } catch {
    return "sistema";
  }
};

type Pendiente = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  estado_global: string | null;
  motivo: string;
  solicitado_at: string | null;
  solicitado_por: string | null;
};

type TrazFila = {
  proyecto_id: number;
  codigo: string;
  nombre: string;
  estado_global: string | null;
  redisenno_workflow: Record<string, any>;
  revision_updated_at: string | null;
};

export default function RedisennoAprobacionesPanel() {
  const navigate = useNavigate();
  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getToken(), []);

  const [mainTab, setMainTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingTraz, setLoadingTraz] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<Pendiente[]>([]);
  const [trazItems, setTrazItems] = React.useState<TrazFila[]>([]);
  const [comentarios, setComentarios] = React.useState<Record<number, string>>({});
  const [resolviendo, setResolviendo] = React.useState<number | null>(null);

  const cargarPendientes = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${baseUrl}/redisenno/pendientes`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar las solicitudes.");
      setItems(await res.json());
    } catch (e: any) {
      setError(e.message || "Error al cargar.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  const cargarTrazabilidad = React.useCallback(async () => {
    try {
      setLoadingTraz(true);
      setError(null);
      const res = await fetch(`${baseUrl}/redisenno/trazabilidad`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No fue posible cargar la trazabilidad.");
      setTrazItems(await res.json());
    } catch (e: any) {
      setError(e.message || "Error al cargar trazabilidad.");
    } finally {
      setLoadingTraz(false);
    }
  }, [baseUrl, token]);

  React.useEffect(() => {
    cargarPendientes();
  }, [cargarPendientes]);

  React.useEffect(() => {
    if (mainTab === 1) cargarTrazabilidad();
  }, [mainTab, cargarTrazabilidad]);

  const resolver = async (id: number, decision: "aprobado" | "rechazado") => {
    try {
      setResolviendo(id);
      setError(null);
      const res = await fetch(`${baseUrl}/${id}/redisenno/resolucion`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          decision,
          comentario_admin: comentarios[id] || "",
          updated_by: getUsuarioSesion(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "No fue posible registrar la resolución.");
      }
      await cargarPendientes();
      if (mainTab === 1) await cargarTrazabilidad();
    } catch (e: any) {
      setError(e.message || "Error al resolver.");
    } finally {
      setResolviendo(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Aprobaciones de rediseño
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Revise solicitudes pendientes o consulte la trazabilidad por proyecto (solicitud, aprobación o rechazo por
        administración e historial conservado).
      </Typography>

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab label="Pendientes de aprobación" />
        <Tab label="Trazabilidad por proyecto" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mainTab === 0 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={cargarPendientes} disabled={loading}>
              Refrescar
            </Button>
          </Stack>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Cargando…</Typography>
            </Stack>
          ) : items.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography color="text.secondary">No hay solicitudes de rediseño pendientes.</Typography>
            </Paper>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Proyecto</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Solicitante</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Comentario admin (opcional)</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.proyecto_id}>
                    <TableCell>{row.nombre}</TableCell>
                    <TableCell>{row.codigo}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{row.motivo || "—"}</TableCell>
                    <TableCell>{row.solicitado_por || "—"}</TableCell>
                    <TableCell>
                      {row.solicitado_at ? new Date(row.solicitado_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Observación interna"
                        value={comentarios[row.proyecto_id] || ""}
                        onChange={(e) =>
                          setComentarios((prev) => ({ ...prev, [row.proyecto_id]: e.target.value }))
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="column" spacing={0.5} alignItems="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={resolviendo === row.proyecto_id}
                          onClick={() => resolver(row.proyecto_id, "aprobado")}
                        >
                          Aprobar rediseño
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={resolviendo === row.proyecto_id}
                          onClick={() => resolver(row.proyecto_id, "rechazado")}
                        >
                          Rechazar
                        </Button>
                        <Button size="small" onClick={() => navigate(`/proyectos-panel/${row.proyecto_id}/gestion`)}>
                          Abrir gestión
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {mainTab === 1 && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={cargarTrazabilidad} disabled={loadingTraz}>
              Refrescar trazabilidad
            </Button>
          </Stack>
          {loadingTraz ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2">Cargando trazabilidad…</Typography>
            </Stack>
          ) : trazItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography color="text.secondary">
                No hay proyectos con trazabilidad de rediseño registrada.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {trazItems.map((row) => (
                <Accordion key={row.proyecto_id} defaultExpanded={false} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ pr: 1 }}>
                      <Typography fontWeight={800}>{row.codigo}</Typography>
                      <Typography color="text.secondary">{row.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Última actualización checklist:{" "}
                        {row.revision_updated_at
                          ? new Date(row.revision_updated_at).toLocaleString("es-CO")
                          : "—"}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RedisennoTrazabilidadVista data={row.redisenno_workflow} />
                    <Button
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/proyectos-panel/${row.proyecto_id}/gestion`)}
                    >
                      Abrir gestión del proyecto
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
